
import { Button, Select, Switch, TimePicker } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import axios from "utils/axios";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import styled from "styled-components";
import configFile from "config.json";
import dayjs from "dayjs";
const API_BASE = `${configFile.PROD_SERVER_URL}/menu2/schedule`;
const dayLabels = [
    "Ням",
    "Даваа",
    "Мягмар",
    "Лхагва",
    "Пүрэв",
    "Баасан",
    "Бямба",
];
const dayOptions = dayLabels.map((label, index) => ({
    label,
    value: index,
}));

const scheduleTemplate = {
    dayOfWeek: 1,
    isAble: true,
    endsNextDay: false,
    start: dayjs("09:00", "HH:mm"),
    end: dayjs("22:00", "HH:mm"),
};

const AvailabilityTab = ({ menuId }) => {
    const [availabilityDates, setAvailabilityDates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const isDraft = !menuId || String(menuId).startsWith("draft_");

    const normalizeTime = (value) => {
        if (!value) {
            return null;
        }
        if (dayjs.isDayjs(value)) {
            return value;
        }
        return dayjs(value, "HH:mm");
    };

    const normalizeDayOfWeek = (value) => {
        if (typeof value === "number") {
            return value;
        }
        const index = dayLabels.indexOf(value);
        return index >= 0 ? index : 0;
    };

    const normalizeRow = (row) => ({
        id: row?.id,
        referenceId: row?.referenceId,
        dayOfWeek: normalizeDayOfWeek(row?.dayOfWeek ?? row?.day),
        isAble: row?.isAble ?? true,
        endsNextDay: row?.endsNextDay ?? false,
        start: normalizeTime(row?.startTime ?? row?.start) || scheduleTemplate.start,
        end: normalizeTime(row?.endTime ?? row?.end) || scheduleTemplate.end,
    });

    const mergeWithDefaults = (rows) => (rows || []).map(normalizeRow);
    const sortByDayOfWeek = (rows) =>
        [...rows].sort((a, b) => a.dayOfWeek - b.dayOfWeek);

    const fetchSchedule = async (referenceId) => {
        if (!referenceId || isDraft) {
            setAvailabilityDates([]);
            return;
        }
        setLoading(true);
        try {
            const { data: response } = await axios.post(
                `${API_BASE}/getScheduleByReferenceId`,
                {
                    referenceId,
                }
            );
            if (response?.data?.length > 0) {
                setAvailabilityDates(sortByDayOfWeek(mergeWithDefaults(response.data)));
            } else {
                setAvailabilityDates([]);
            }
        } catch (err) {
            NotificationManager.error(err?.message || "Амжилтгүй");
            setAvailabilityDates([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSchedule(menuId);
    }, [menuId]);

    const handleRangeChange = (value, index) => {
        setAvailabilityDates((prev) =>
            prev.map((item, i) =>
                index === i
                    ? { ...item, start: value?.[0] || null, end: value?.[1] || null }
                    : item
            )
        );
    };

    const handleDayChange = (value, index) => {
        setAvailabilityDates((prev) =>
            sortByDayOfWeek(
                prev.map((item, i) =>
                    index === i ? { ...item, dayOfWeek: value } : item
                )
            )
        );
    };

    const handleToggleSpans = (checked, index) => {
        setAvailabilityDates((prev) =>
            prev.map((item, i) =>
                index === i ? { ...item, endsNextDay: checked } : item
            )
        );
    };

    const handleToggleAble = (checked, index) => {
        setAvailabilityDates((prev) =>
            prev.map((item, i) => (index === i ? { ...item, isAble: checked } : item))
        );
    };

    const handleDeleteSchedule = async (scheduleId, index) => {
        if (!scheduleId) {
            setAvailabilityDates((prev) => prev.filter((_, i) => i !== index));
            return;
        }
        if (isDraft) {
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${API_BASE}/removeScheduleById`, { id: scheduleId });
            await fetchSchedule(menuId);
            NotificationManager.success("Амжилттай");
        } catch (err) {
            NotificationManager.error(err?.message || "Амжилтгүй");
        } finally {
            setSaving(false);
        }
    };

    const handleAddSchedule = () => {
        setAvailabilityDates((prev) => {
            const usedDays = new Set(prev.map((item) => item.dayOfWeek));
            const nextDay =
                dayOptions.find((option) => !usedDays.has(option.value))?.value ??
                scheduleTemplate.dayOfWeek;
            return sortByDayOfWeek([
                ...prev,
                { ...scheduleTemplate, dayOfWeek: nextDay },
            ]);
        });
    };

    const handleSaveAvailability = async () => {
        if (isDraft) {
            NotificationManager.error("Эхлээд бүтээгдэхүүнээ хадгална уу.");
            return;
        }
        setSaving(true);
        try {
            const payload = availabilityDates.map((item) => ({
                id: item?.id,
                referenceId: menuId,
                dayOfWeek: item.dayOfWeek,
                isAble: !!item.isAble,
                startTime: item.start ? dayjs(item.start).format("HH:mm") : null,
                endTime: item.end ? dayjs(item.end).format("HH:mm") : null,
                endsNextDay: !!item.endsNextDay,
            }));
            const createRows = payload.filter((item) => !item.id);
            const updateRows = payload.filter((item) => item.id);
            if (createRows.length > 0) {
                await axios.post(`${API_BASE}/createSchedules`, {
                    schedules: createRows,
                });
            }
            if (updateRows.length > 0) {
                await axios.post(`${API_BASE}/updateSchedules`, {
                    schedules: updateRows,
                });
            }
            await fetchSchedule(menuId);
            NotificationManager.success("Амжилттай");
        } catch (err) {
            NotificationManager.error(err?.message || "Амжилтгүй");
        } finally {
            setSaving(false);
        }
    };

    return (
        <TabRoot>
            <ScheduleCard>
                <HeaderRow>
                    <SectionTitle>7 хоногийн хуваарь</SectionTitle>
                    <Button onClick={handleAddSchedule}>Хуваарь нэмэх</Button>
                </HeaderRow>
                <ScheduleHeader>
                    <span>Өдөр</span>
                    <span>Цагийн интервал</span>
                    <span>Идэвхтэй</span>
                    <span style={{ whiteSpace: "nowrap" }}>Дараа өдөр дуусах</span>
                    <span>Устгах</span>
                </ScheduleHeader>
                <ScheduleList>
                    {availabilityDates.map((item, index) => {
                        return (
                            <ScheduleRow
                                key={`${item.dayOfWeek}-${index}`}
                                $isNew={!item.id}
                            >
                                <DaySelectWrap>
                                    <DaySelect
                                        value={item.dayOfWeek}
                                        options={dayOptions}
                                        onChange={(value) => handleDayChange(value, index)}
                                    />
                                </DaySelectWrap>
                                <TimePicker.RangePicker
                                    format="HH:mm"
                                    minuteStep={15}
                                    value={[item.start, item.end]}
                                    onChange={(value) =>
                                        handleRangeChange(value, index)
                                    }
                                    disabled={loading || !item.isAble}
                                    style={{ width: "100%" }}
                                />
                                <Switch
                                    style={{ maxWidth: '80px', margin: '0 auto' }}
                                    checked={item.isAble}
                                    onChange={(checked) =>
                                        handleToggleAble(checked, index)
                                    }
                                />
                                <Switch
                                    style={{ maxWidth: '80px', margin: '0 auto' }}
                                    checked={item.endsNextDay}
                                    onChange={(checked) =>
                                        handleToggleSpans(checked, index)
                                    }
                                />
                                <DeleteButton
                                    danger
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleDeleteSchedule(item.id, index)}
                                    disabled={saving}
                                />
                            </ScheduleRow>
                        )
                    })}
                </ScheduleList>
            </ScheduleCard>

            <Actions>
                <Button
                    type="primary"
                    onClick={handleSaveAvailability}
                    loading={saving}
                    disabled={loading}
                >
                    Хадгалах
                </Button>
            </Actions>
        </TabRoot>
    )
};

export default AvailabilityTab


const TabRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ScheduleCard = styled.div`
  background: #f6f7fb;
  border: 1px solid #e4e7f0;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ScheduleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ScheduleHeader = styled.div`
  display: grid;
  grid-template-columns: 140px minmax(240px, 1fr) 100px 100px auto;
  align-items: center;
  gap: 12px;
  padding: 0 12px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #6b7280;

  @media (max-width: 720px) {
    display: none;
  }
`;

const ScheduleRow = styled.div`
  display: grid;
  grid-template-columns: 140px minmax(240px, 1fr) 100px 100px auto;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #e6e8ef;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: ${({ $isNew }) =>
    $isNew ? "0 0 0 2px rgba(59, 130, 246, 0.2)" : "none"};
  border-color: ${({ $isNew }) =>
    $isNew ? "rgba(59, 130, 246, 0.45)" : "#e6e8ef"};
  background: ${({ $isNew }) =>
    $isNew ? "rgba(239, 246, 255, 0.7)" : "#ffffff"};

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    align-items: flex-start;
  }
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.2px;
`;

const DaySelect = styled(Select)`
  width: 100%;
`;

const DaySelectWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const DeleteButton = styled(Button)`
  color: #ef4444;
`;
